import React, {
  useEffect,
  useState,
  useMemo,
  Suspense,
  lazy,
  useRef
} from "react";
import { BiBriefcaseAlt } from "react-icons/bi";
import {
  LuUsers,
  LuBuilding2,
  LuCreditCard,
  LuChevronDown,
  LuDollarSign,
  LuFileText,
  LuUserCheck,
  LuShieldCheck,
  LuCheck
} from "react-icons/lu";
import {
  APPLY_ID_STATUS_POLL_INTERVAL,
  COMPLETED,
  INPROGRESS,
  PROPERTY_OWNED,
  START,
  USERS_ROLE,
  WAITING_START
} from "../../../utils/constants/appConstants";
import "./steps-style.scss";
import {
  buttonName,
  newOnboardingSteps,
  responseStatus
} from "../../../locales/en";
import privateAxios from "../../../utils/axiosPrivate";
import { getCreateOnboardingStepURL } from "../../../utils/constants/apiEndPoints";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchApplyIdStatus,
  getAboutYourBusiness,
  getAnnatureCheckStatus,
  getBusinessDetailsData,
  getOnboardingOwnerData,
  getOnboardingRoleData,
  getOnboardingStepsByUser
} from "../../../redux/onboardingSlice";
import { showToast } from "../../../utils/utils";
import { mapBusinessTypeFromPayload } from "../../../utils/constants/businessTypeConfig";
import { getConnectedSoftware } from "../../../redux/loginSlice";

// Lazy load components for code splitting
const Business = lazy(() => import("./business"));
const Role = lazy(() => import("./role"));
const Identity = lazy(() => import("./identity"));
const PeopleBusiness = lazy(() => import("./people-business"));
const PropertyDetails = lazy(() => import("./property-details"));
const SupportingCompanyDetails = lazy(
  () => import("./supporting-company-details")
);
const TrustDetails = lazy(() => import("./trust-details"));
const SupportingTrustDetails = lazy(() => import("./supporting-trust-details"));
const Repayment = lazy(() => import("./repayment"));
const Agree = lazy(() => import("./agree"));
const VerifyId = lazy(() => import("./verify-id"));
const WhatYouAgree = lazy(() => import("./what-you-agree"));

// Loading Fallback Component
const StepLoadingFallback = () => (
  <div className="steps-loading-fallback">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">{buttonName?.loading}</span>
    </div>
  </div>
);

const Steps = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.userManagement?.profile?.id);
  const {
    onBoardingStepsData,
    onboardingRoleData,
    applyIdStatusData,
    aboutYourBusinessData,
    businessDetailsData,
    ownersData
  } = useSelector((state) => state.onboarding);
  const intervalRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [tabFacilitySize, setTabFacilitySize] = useState(null);
  const [tabUserRole, setTabUserRole] = useState("");
  const [tabBusinessType, setTabBusinessType] = useState("Limited Company");
  const [tabTrustName, setTabTrustName] = useState("");
  const [tabPropertyOwned, setTabPropertyOwned] = useState(
    PROPERTY_OWNED?.PERSONALLY_OWNED
  );
  const [tabPropertyTrustName, setTabPropertyTrustName] = useState("");
  const [tabPropertyCompanyName, setTabPropertyCompanyName] = useState("");
  const [loadedTabs, setLoadedTabs] = useState(new Set([0]));
  const autoAdvancedStepsRef = React.useRef(new Set());
  const isManualStepClickRef = useRef(false);
  const stepRefs = useRef({});

  useEffect(() => {
    if (onboardingRoleData) {
      setTabUserRole(onboardingRoleData.yourRole || "");
    }
  }, [onboardingRoleData]);

  useEffect(() => {
    if (aboutYourBusinessData) {
      setTabFacilitySize(aboutYourBusinessData.facilitySize || null);
    }
  }, [aboutYourBusinessData]);

  useEffect(() => {
    if (!businessDetailsData) return;

    setTabBusinessType(
      mapBusinessTypeFromPayload(businessDetailsData.businessType)
    );
    setTabTrustName(businessDetailsData.trustName || "");
  }, [businessDetailsData]);

  useEffect(() => {
    if (!ownersData) return;

    setTabPropertyOwned(
      ownersData.ownershipType || PROPERTY_OWNED?.PERSONALLY_OWNED
    );
    setTabPropertyTrustName(ownersData.trustEntityName || "");
    setTabPropertyCompanyName(ownersData.companyName || "");
  }, [ownersData]);

  // Memoize conditional values to prevent unnecessary recalculations
  const isLargeFacility = useMemo(
    () => ["30-100", "100-250", "above-250"].includes(tabFacilitySize),
    [tabFacilitySize]
  );

  const isOwner = useMemo(
    () => tabUserRole === USERS_ROLE.OWNER_OR_DIRECTOR,
    [tabUserRole]
  );

  const isOwnerConsentAccepted = useMemo(
    () => isOwner && !!onboardingRoleData?.isCreditConsentAccepted,
    [isOwner, onboardingRoleData]
  );

  const isEmployeeOrAdvisor = useMemo(
    () =>
      tabUserRole === USERS_ROLE.EMPLOYEE ||
      tabUserRole === USERS_ROLE.EXTERNAL_ADVISOR,
    [tabUserRole]
  );

  const isWaitingCase = useMemo(
    () => isLargeFacility && isEmployeeOrAdvisor,
    [isLargeFacility, isEmployeeOrAdvisor]
  );

  const isBusinessTrustOwned = useMemo(
    () => tabBusinessType === "Trading Trust" && !!tabTrustName?.trim(),
    [tabBusinessType, tabTrustName]
  );

  const isPropertyTrustOwned = useMemo(
    () =>
      tabPropertyOwned === PROPERTY_OWNED?.OWNED_BY_TRUST &&
      !!tabPropertyTrustName?.trim(),
    [tabPropertyOwned, tabPropertyTrustName]
  );

  const isCompanyOwned = useMemo(
    () =>
      tabPropertyOwned === PROPERTY_OWNED?.OWNED_BY_COMPANY &&
      !!tabPropertyCompanyName?.trim(),
    [tabPropertyOwned, tabPropertyCompanyName]
  );

  const isShareholderCompanyRequired = useMemo(
    () => Boolean(businessDetailsData?.supportingCompanyRequired),
    [businessDetailsData]
  );

  const shareholderCompanyName = useMemo(() => {
    const isCompanyHeld = (shareholder) => {
      const type = String(shareholder?.shareholdingType || "")
        .replace(/\s/g, "")
        .toLowerCase();
      return type === "heldbyacompany" || type === "companyheld";
    };
    const findCompanyShareholder = (list) =>
      (Array.isArray(list) ? list : []).find(
        (shareholder) =>
          isCompanyHeld(shareholder) &&
          parseFloat(shareholder?.share_percentage) >= 25 &&
          shareholder?.companyName?.trim()
      );
    // A shareholder edited through the per-person update endpoint can live only
    // in shareholderAndDirector.shareholders (e.g. an NZBN-lookup-seeded roster,
    // where the top-level list is empty and the edit cannot be mirrored into
    // it), so both lists must be searched to resolve the company name.
    const companyShareholder =
      findCompanyShareholder(businessDetailsData?.shareholders) ||
      findCompanyShareholder(
        businessDetailsData?.shareholderAndDirector?.shareholders
      );
    return companyShareholder?.companyName || "";
  }, [businessDetailsData]);

  const supportingCompanyName = isCompanyOwned
    ? tabPropertyCompanyName
    : shareholderCompanyName;

  const steps = Array.isArray(onBoardingStepsData) ? onBoardingStepsData : [];

  const stepStatusMap = useMemo(() => {
    return steps.reduce((acc, item) => {
      if (item?.step) {
        acc[item.step] = item.status;
      }
      return acc;
    }, {});
  }, [steps]);

  const isStepCompleted = (title) =>
    stepStatusMap[title]?.toLowerCase() === COMPLETED.toLowerCase();

  const isStepInProgress = (title) =>
    stepStatusMap[title]?.toLowerCase() === INPROGRESS.toLowerCase();

  // Agreements are filled with the legal name the ID check captures, so nothing
  // signable is offered until the verification has actually completed - the
  // live ApplyID status (shared by whoever verifies on this account, so it also
  // covers the director an Employee/Advisor is waiting on), or the Verify
  // Identity step already recorded as done.
  const isIdentityVerified = useMemo(
    () =>
      Boolean(applyIdStatusData?.status) ||
      stepStatusMap["Verify Identity"]?.toLowerCase() ===
        COMPLETED.toLowerCase(),
    [applyIdStatusData, stepStatusMap]
  );

  const resolveStepName = (step) => {
    if (step?.component === SupportingTrustDetails) {
      return "Property Trust Details";
    }
    if (step?.component === TrustDetails) {
      return "Trading Trust Details";
    }
    return step?.title;
  };

  // Memoize requiredSteps to prevent unnecessary re-renders
  const requiredSteps = useMemo(
    () => [
      {
        icon: <BiBriefcaseAlt />,
        title: "About Your Business",
        status: stepStatusMap["About Your Business"],
        component: Business,
        props: {
          setTabFacilitySize,
          onContinue: () => handleStepComplete("About Your Business")
        }
      },
      {
        icon: <LuUsers />,
        title: "Your Role",
        component: Role,
        status: stepStatusMap["Your Role"],
        props: {
          onContinue: () => handleStepComplete("Your Role")
        }
      },
      ...(isOwnerConsentAccepted
        ? [
            {
              icon: <LuUserCheck />,
              title: "Verify Identity",
              status: stepStatusMap["Verify Identity"],
              component: Identity,
              props: {
                onContinue: () => handleStepComplete("Verify Identity")
              }
            }
          ]
        : []),
      {
        icon: <LuBuilding2 />,
        title: "People In The Business",
        status: stepStatusMap["People In The Business"],
        component: PeopleBusiness,
        props: {
          setTabBusinessType,
          setTabTrustName,
          onContinue: () => handleStepComplete("People In The Business")
        }
      },
      ...(isLargeFacility
        ? [
            {
              icon: <LuDollarSign />,
              title: "Property Details",
              component: PropertyDetails,
              props: {
                setTabPropertyOwned,
                setTabPropertyTrustName,
                setTabPropertyCompanyName,
                onContinue: () => handleStepComplete("Property Details")
              }
            }
          ]
        : []),
      ...(isCompanyOwned || isShareholderCompanyRequired
        ? [
            {
              icon: <LuFileText />,
              title: "Supporting Company Details",
              subtitle: supportingCompanyName,
              status: stepStatusMap["Supporting Company Details"],
              component: SupportingCompanyDetails,
              props: {
                companyName: supportingCompanyName,
                onContinue: () =>
                  handleStepComplete("Supporting Company Details")
              }
            }
          ]
        : []),
      ...(isPropertyTrustOwned
        ? [
            {
              icon: <LuFileText />,
              title: "Trust Details",
              subtitle: tabPropertyTrustName,
              status: stepStatusMap["Trust Details"],
              component: SupportingTrustDetails,
              props: {
                trustName: tabPropertyTrustName,
                onContinue: () =>
                  handleStepComplete("Trust Details", SupportingTrustDetails)
              }
            }
          ]
        : []),
      ...(isBusinessTrustOwned
        ? [
            {
              icon: <LuFileText />,
              title: "Trust Details",
              subtitle: tabTrustName,
              status: stepStatusMap["Trust Details"],
              component: TrustDetails,
              props: {
                trustName: tabTrustName,
                onContinue: () =>
                  handleStepComplete("Trust Details", TrustDetails)
              }
            }
          ]
        : []),
      {
        icon: <LuCreditCard />,
        title: "Funding and Repayments",
        component: Repayment,
        props: {
          onContinue: () => handleStepComplete("Funding and Repayments")
        }
      },
      ...(isLargeFacility && isOwner && isIdentityVerified
        ? [
            {
              icon: <LuFileText />,
              title: "What You Agree To",
              status: stepStatusMap["What You Agree To"],
              component: Agree,
              props: {
                onContinue: () => handleStepComplete("What You Agree To")
              }
            }
          ]
        : [])
    ],
    [
      isLargeFacility,
      isOwner,
      isOwnerConsentAccepted,
      isIdentityVerified,
      isBusinessTrustOwned,
      isPropertyTrustOwned,
      isCompanyOwned,
      isShareholderCompanyRequired,
      supportingCompanyName,
      tabTrustName,
      tabPropertyTrustName,
      tabPropertyCompanyName,
      stepStatusMap
    ]
  );

  // Memoize waitingSteps to prevent unnecessary re-renders
  const waitingSteps = useMemo(
    () =>
      isWaitingCase
        ? [
            {
              icon: <LuShieldCheck />,
              title: "Verify Identity",
              subtitle: "Director 1",
              component: VerifyId,
              completed: isIdentityVerified,
              props: {}
            },
            // The agreement card only appears once the director's ID check is
            // done - the docs are filled with the legal name that check provides.
            ...(isIdentityVerified
              ? [
                  {
                    icon: <LuFileText />,
                    title: "What You Agree To",
                    subtitle: "Director 1",
                    component: WhatYouAgree,
                    props: {}
                  }
                ]
              : [])
          ]
        : [],
    [isWaitingCase, isIdentityVerified]
  );

  const toggleAccordion = (index, label, component) => {
    const isOpening = activeIndex !== index;
    isManualStepClickRef.current = true;
    setActiveIndex(isOpening ? index : null);
    if (isOpening) {
      setLoadedTabs((prev) => new Set(prev).add(index));
      if (label) {
        handleStepClick(label, component);
      }
    }
  };

  const handleStepClick = async (step, component) => {
    let stepName = step;
    if (component === SupportingTrustDetails) {
      stepName = "Property Trust Details";
    } else if (component === TrustDetails) {
      stepName = "Trading Trust Details";
    }

    const existingStep = steps
      ? steps.find(
          (item) =>
            item?.step === stepName &&
            (item?.status?.toLowerCase() === INPROGRESS.toLowerCase() ||
              item?.status?.toLowerCase() === COMPLETED.toLowerCase())
        )
      : undefined;

    if (existingStep) {
      return;
    }

    try {
      const payload = {
        userAccountId: userId,
        step: stepName
      };

      await privateAxios.put(getCreateOnboardingStepURL, payload);
      dispatch(getOnboardingStepsByUser(Number(userId)));
    } catch (error) {
      showToast(
        error?.response?.data?.message ?? responseStatus?.errorText,
        responseStatus?.error
      );
    }
  };

  const handleStepComplete = async (step, component) => {
    let stepName = step;
    if (component === SupportingTrustDetails) {
      stepName = "Property Trust Details";
    } else if (component === TrustDetails) {
      stepName = "Trading Trust Details";
    }

    try {
      const payload = {
        userAccountId: userId,
        step: stepName,
        status: COMPLETED
      };

      await privateAxios.put(getCreateOnboardingStepURL, payload);
      dispatch(getOnboardingStepsByUser(Number(userId)));
    } catch (error) {
      showToast(
        error?.response?.data?.message ?? responseStatus?.errorText,
        responseStatus?.error
      );
    }
  };

  useEffect(() => {
    if (activeIndex === null || activeIndex === undefined) return;

    if (isManualStepClickRef.current) {
      isManualStepClickRef.current = false;
      return;
    }

    const currentStep = requiredSteps[activeIndex];
    if (!currentStep) return;

    if (
      isStepCompleted(resolveStepName(currentStep)) &&
      !autoAdvancedStepsRef.current.has(activeIndex)
    ) {
      autoAdvancedStepsRef.current.add(activeIndex);

      let nextIndex = activeIndex + 1;
      let nextStep = requiredSteps[nextIndex];
      while (nextStep && isStepCompleted(resolveStepName(nextStep))) {
        nextIndex += 1;
        nextStep = requiredSteps[nextIndex];
      }

      if (!nextStep) {
        setActiveIndex(null);
        return;
      }

      setActiveIndex(nextIndex);
      setLoadedTabs((prev) => new Set(prev).add(nextIndex));
      handleStepClick(nextStep.title, nextStep.component);
    }
  }, [activeIndex, requiredSteps, stepStatusMap]);

  const hasRedirectedToDashboardRef = useRef(false);

  useEffect(() => {
    if (hasRedirectedToDashboardRef.current) return;

    // The step list is built from role/facility data that loads asynchronously;
    // until both have arrived it can be missing conditional steps, and a
    // half-built list must never read as "everything done".
    if (!onboardingRoleData || !aboutYourBusinessData) return;
    if (!steps.length) return;

    const allTasksCompleted = requiredSteps.every((step) =>
      isStepCompleted(resolveStepName(step))
    );
    if (!allTasksCompleted) return;

    hasRedirectedToDashboardRef.current = true;
    showToast(newOnboardingSteps?.allTasksCompleted, responseStatus?.success);
    navigate("/invoice");
  }, [
    requiredSteps,
    stepStatusMap,
    onboardingRoleData,
    aboutYourBusinessData,
    steps.length,
    navigate
  ]);

  useEffect(() => {
    if (activeIndex === null || activeIndex === undefined) return;

    const node = stepRefs.current[activeIndex];
    if (!node) return;

    const scrollTimeout = setTimeout(() => {
      node.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }, 150);

    return () => clearTimeout(scrollTimeout);
  }, [activeIndex]);

  useEffect(() => {
    if (userId) {
      dispatch(getAboutYourBusiness(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(getBusinessDetailsData(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(getOnboardingRoleData(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(getOnboardingStepsByUser(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId && requiredSteps.length) {
      handleStepClick(requiredSteps[0].title, requiredSteps[0].component);
    }
  }, [userId]);

  useEffect(() => {
    dispatch(fetchApplyIdStatus());
  }, [dispatch]);

  useEffect(() => {
    if (userId) {
      dispatch(getOnboardingOwnerData(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(getAnnatureCheckStatus(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (
      applyIdStatusData?.status === false &&
      applyIdStatusData?.isDemoAccount === false
    ) {
      intervalRef.current = setInterval(() => {
        dispatch(fetchApplyIdStatus());
      }, APPLY_ID_STATUS_POLL_INTERVAL);
    }
    return () => clearInterval(intervalRef.current);
  }, [applyIdStatusData, dispatch]);

  useEffect(() => {
    dispatch(getConnectedSoftware());
  }, [dispatch]);

  return (
    <div className="steps-wrap">
      {requiredSteps.map((step, index) => {
        const Component = step.component;
        const stepName = resolveStepName(step);
        const isTabLoaded = loadedTabs.has(index);
        const isCompleted = isStepCompleted(stepName);
        const isInProgress = isStepInProgress(stepName);

        return (
          <div
            className={`steps-container ${activeIndex === index ? "active-step" : ""}`}
            key={index}
            ref={(el) => {
              stepRefs.current[index] = el;
            }}
          >
            <div className="steps-title-wrap">
              <div
                onClick={() => toggleAccordion(index, step.title, Component)}
                className="steps-title"
              >
                <div className="left-section">
                  <span
                    className={`steps-icon ${isCompleted ? "completed-icon" : ""}`}
                  >
                    {isCompleted ? <LuCheck /> : step.icon}
                  </span>
                  <div className="title-content">
                    {` ${step.title}`}
                    <small className="text-muted">{step.subtitle}</small>
                  </div>
                </div>
                <div className="right-section">
                  <span
                    className={`status-progress me-2 ${activeIndex === index ? "active-status" : ""} ${isCompleted ? "completed-status" : ""} ${isInProgress ? "inprogress-status" : ""}`}
                  >
                    {stepStatusMap[stepName] || START}
                  </span>
                  <span className="dropdown-arrow">
                    <LuChevronDown />
                  </span>
                </div>
              </div>
            </div>

            {isTabLoaded && (
              <div
                className={`steps-content-collapse ${activeIndex === index ? "is-open" : ""}`}
              >
                <div className="steps-content">
                  <div className="steps-content-inner">
                    <Suspense fallback={<StepLoadingFallback />}>
                      <Component
                        {...step.props}
                        status={stepStatusMap[stepName]}
                      />
                    </Suspense>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {waitingSteps.length > 0 && (
        <>
          <div className="label-title mt-4">
            <h3>{newOnboardingSteps?.waitingStepTitle}</h3>
          </div>

          {waitingSteps.map((step, index) => {
            const waitingIndex = requiredSteps.length + index;
            const Component = step.component;
            const isTabLoaded = loadedTabs.has(waitingIndex);

            return (
              <div
                className={`steps-container ${activeIndex === waitingIndex ? "active-step" : ""}`}
                key={waitingIndex}
                ref={(el) => {
                  stepRefs.current[waitingIndex] = el;
                }}
              >
                <div className="steps-title-wrap">
                  <div
                    onClick={() =>
                      toggleAccordion(waitingIndex, step.title, Component)
                    }
                    className="steps-title"
                  >
                    <div className="left-section">
                      <span
                        className={`steps-icon ${step.completed ? "completed-icon" : ""}`}
                      >
                        {step.completed ? <LuCheck /> : step.icon}
                      </span>
                      <div className="title-content">
                        {` ${step.title}`}
                        <small className="text-muted">{step.subtitle}</small>
                      </div>
                    </div>

                    <div className="right-section">
                      <span
                        className={`status-progress me-2 ${step.completed ? "completed-status" : "waiting-status"}`}
                      >
                        {step.completed ? COMPLETED : WAITING_START}
                      </span>

                      <span className="dropdown-arrow">
                        <LuChevronDown />
                      </span>
                    </div>
                  </div>
                </div>

                {isTabLoaded && (
                  <div
                    className={`steps-content-collapse ${activeIndex === waitingIndex ? "is-open" : ""}`}
                  >
                    <div className="steps-content">
                      <div className="steps-content-inner">
                        <Suspense fallback={<StepLoadingFallback />}>
                          <Component
                            {...step.props}
                            status={stepStatusMap[step.title]}
                          />
                        </Suspense>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default Steps;
